import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MAPBOX_TOKEN } from '../firebaseConfig';
import { getFirestore, collection, getDocs, doc, getDoc } from 'firebase/firestore';
import './MapView.css';

// IMPORTANT: mapbox-gl is intentionally NOT imported from npm/webpack here.
// It's loaded as a global <script> tag from the Mapbox CDN in public/index.html.
// Bundling it through Create React App's production Webpack + Terser pipeline
// corrupts the minified variable scoping inside mapbox-gl's Web Worker code
// (used for parsing vector tiles), causing "ReferenceError: x is not defined"
// / "Cannot read properties of undefined" errors deep in tile parsing. The
// map still initializes and responds to pan/zoom (so markers render), but
// base map tiles never paint — a blank gray canvas. Using the untouched CDN
// build avoids Webpack touching it at all. If you bump the version here, bump
// the matching <script>/<link> tags in public/index.html too.
const mapboxgl = window.mapboxgl;
if (mapboxgl) {
  mapboxgl.accessToken = MAPBOX_TOKEN;
}

const db = getFirestore();

const MapView = ({ stores: _ignoreStores, userLocation, favorites = [], userFodyes = { balance: 0, backingValue: 0 }, tags = [], selectedTags = [], toggleTag, clearFilters, TAG_ICON_MAP = {} }) => {
  const navigate = useNavigate();
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markersRef = useRef([]);
  const userMarkerRef = useRef(null);
  const [allStores, setAllStores] = useState([]);
  const [visibleStores, setVisibleStores] = useState([]);
  const [loadingMap, setLoadingMap] = useState(true);
  const [mapError, setMapError] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const [storeProducts, setStoreProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  // Mobile bottom sheet states: 'collapsed' | 'half' | 'full'
  const [sheetState, setSheetState] = useState('collapsed');
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'favorites'
  const [showAllHours, setShowAllHours] = useState(false);

  const favoriteIds = useMemo(() => new Set(favorites.map((f) => f.storeId)), [favorites]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Safety: re-enable map interactions whenever sheetState changes (drag ended)
  useEffect(() => {
    if (map.current) {
      try { map.current.dragPan.enable(); } catch (_) {}
      try { map.current.scrollZoom.enable(); } catch (_) {}
    }
  }, [sheetState]);

  // Haversine distance in miles
  const getDistance = (lat1, lng1, lat2, lng2) => {
    const R = 3959;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // 1. Load ALL published stores once
  useEffect(() => {
    let cancelled = false;
    const loadAllStores = async () => {
      setLoadingMap(true);
      try {
        const snap = await getDocs(collection(db, 'publicStores'));
        let results = [];
        snap.docs.forEach((docItem) => {
          const data = docItem.data();
          if (data.published && data.latitude && data.longitude) {
            results.push({
              id: docItem.id,
              ...data,
            });
          }
        });
        // Load ratings in parallel
        await Promise.all(
          results.map(async (store) => {
            try {
              const statsDoc = await getDoc(doc(db, 'publicStores', store.id, 'reviewStats', 'summary'));
              if (statsDoc.exists()) {
                const s = statsDoc.data();
                store.averageRating = s.averageRating || 0;
                store.totalReviews = s.totalReviews || 0;
              }
            } catch (e) {
              // ignore
            }
          })
        );
        if (!cancelled) setAllStores(results);
      } catch (e) {
        console.error('Error loading all stores:', e);
      } finally {
        if (!cancelled) setLoadingMap(false);
      }
    };
    loadAllStores();
    return () => { cancelled = true; };
  }, []);

  // Sort: favorites first, then rating desc, then distance to center
  const sortStores = useCallback((stores, centerLat, centerLng) => {
    return [...stores].sort((a, b) => {
      const aFav = favoriteIds.has(a.id);
      const bFav = favoriteIds.has(b.id);
      if (aFav && !bFav) return -1;
      if (!aFav && bFav) return 1;

      const aRating = a.averageRating || 0;
      const bRating = b.averageRating || 0;
      if (bRating !== aRating) return bRating - aRating;

      if (centerLat != null && centerLng != null) {
        const aDist = getDistance(centerLat, centerLng, a.latitude, a.longitude);
        const bDist = getDistance(centerLat, centerLng, b.latitude, b.longitude);
        return aDist - bDist;
      }
      return 0;
    });
  }, [favoriteIds]);

  // 2. Initialize map (runs once loading finishes — even with ZERO stores:
  // an empty catalog is a valid state, not a "still loading" one; the old
  // `allStores.length === 0` guard made the whole map vanish when no
  // restaurant was published).
  useEffect(() => {
    if (!MAPBOX_TOKEN || loadingMap || map.current) return;
    if (!mapboxgl) {
      console.error('[MapView] window.mapboxgl is not available — check that the Mapbox CDN <script> tag in public/index.html loaded successfully.');
      setMapError("Map failed to load (mapbox-gl script didn't load). Check your connection and refresh.");
      return;
    }

    const DEFAULT_CENTER = [-121.275, 37.95]; // Stockton, CA area
    const center = userLocation
      ? [userLocation.lng, userLocation.lat]
      : DEFAULT_CENTER;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v11',
      center,
      zoom: userLocation ? 11 : 9,
    });

    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Surface Mapbox errors (e.g. a 403 from a token URL restriction that
    // doesn't include this domain) instead of failing silently — this is
    // what caused base map tiles to be invisible while markers (plain DOM
    // overlays, unaffected by the token) kept working fine.
    map.current.on('error', (e) => {
      const status = e?.error?.status;
      console.error('[MapView] Mapbox error:', status || '', e?.error?.message || e?.error || e);
      if (status === 401 || status === 403) {
        setMapError(
          "Map tiles couldn't load (Mapbox access issue). Check the token's allowed URLs in the Mapbox dashboard."
        );
      }
    });

    // On map moveend, filter and sort visible stores
    const handleMoveEnd = () => {
      if (!map.current) return;
      const bounds = map.current.getBounds();
      if (!bounds) return;

      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      const center = bounds.getCenter();

      let filtered = allStores.filter((s) => {
        return s.latitude >= sw.lat && s.latitude <= ne.lat && s.longitude >= sw.lng && s.longitude <= ne.lng;
      });

      // Sort by favorites > rating > distance to center
      filtered = sortStores(filtered, center.lat, center.lng);
      // Limit to 40 per viewport
      filtered = filtered.slice(0, 40);

      setVisibleStores(filtered);

      // Update markers: show only visible, remove others
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];

      filtered.forEach((store) => {
        const el = document.createElement('div');
        const isFav = favoriteIds.has(store.id);
        const isSelected = selectedStore?.id === store.id;
        el.className = `ff-map-marker ${isSelected ? 'selected' : ''}`;
        el.dataset.storeId = store.id;
        const name = store.locationName || 'Store';
        el.innerHTML = `
          <div class="ff-map-marker-icon">
            <i class="fas ${isFav ? 'fa-heart' : 'fa-store'}"></i>
          </div>
          <div class="ff-map-marker-name" title="${store.locationName || ''}">${name}</div>
        `;

        const marker = new mapboxgl.Marker(el, { anchor: 'bottom' })
          .setLngLat([store.longitude, store.latitude])
          .addTo(map.current);

        el.addEventListener('click', () => {
          handleSelectStore(store);
        });

        markersRef.current.push(marker);
      });
    };

    map.current.on('moveend', handleMoveEnd);

    // Force mapbox to recalculate canvas size after layout settles
    const forceResize = () => {
      if (map.current) {
        map.current.resize();
      }
    };

    // ROBUST first-load fix: do the initial resize+populate when the STYLE
    // actually finishes loading, not on blind timers. On a cold load (slow
    // network / custom domain) the fixed 100/500ms timers fired BEFORE the
    // style was ready, so Mapbox computed the canvas against an unready
    // style and never painted the base tiles (markers are plain DOM and
    // worked fine) — a manual reload with a warm cache masked it. 'load'
    // fires when the style + initial resources are ready.
    map.current.on('load', () => {
      forceResize();
      // Populate markers/stores now that the style is ready
      setTimeout(() => {
        if (map.current) {
          handleMoveEnd();
          forceResize();
          if (userLocation) {
            updateUserMarker();
          }
        }
      }, 50);
    });

    // Keep the timed resizes as belt-and-suspenders for slow layout settles
    requestAnimationFrame(forceResize);
    const resizeTimer = setTimeout(forceResize, 100);
    const resizeTimer2 = setTimeout(forceResize, 500);

    // Also resize on window resize
    window.addEventListener('resize', forceResize);

    // Initial run after a short delay for map to settle (kept as a fallback —
    // the 'load' handler above is the reliable path for cold loads)
    setTimeout(() => {
      if (map.current) {
        handleMoveEnd();
        forceResize();
        // Show user marker if location already known at init time
        if (userLocation) {
          updateUserMarker();
        }
      }
    }, 600);

    return () => {
      clearTimeout(resizeTimer);
      clearTimeout(resizeTimer2);
      window.removeEventListener('resize', forceResize);
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
      if (userMarkerRef.current) {
        userMarkerRef.current.remove();
        userMarkerRef.current = null;
      }
      if (map.current) {
        map.current.off('moveend', handleMoveEnd);
        map.current.remove();
        map.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allStores, loadingMap, favoriteIds]);

  // Helper: create/update user location marker (blue dot like Flutter app)
  const updateUserMarker = useCallback(() => {
    if (!map.current || !userLocation) return;
    const coords = [userLocation.lng, userLocation.lat];

    if (userMarkerRef.current) {
      userMarkerRef.current.setLngLat(coords);
    } else {
      const el = document.createElement('div');
      el.className = 'ff-user-location-marker';
      el.style.cssText = `
        width: 16px;
        height: 16px;
        background: #2196f3;
        border: 2px solid #fff;
        border-radius: 50%;
        box-shadow: 0 0 0 4px rgba(33,150,243,0.3), 0 2px 4px rgba(0,0,0,0.2);
        cursor: pointer;
      `;
      el.title = 'You are here';
      userMarkerRef.current = new mapboxgl.Marker({ element: el, anchor: 'center' })
        .setLngLat(coords)
        .addTo(map.current);
    }
  }, [userLocation]);

  // 2b. Re-center map smoothly when userLocation changes (address dropdown, etc.)
  // Instead of centering exactly on the house (which may be on the city edge),
  // we calculate the centroid of nearby restaurants (15 km) and fly there.
  // This creates the "pull toward the city" effect seen in DoorDash / Uber Eats.
  useEffect(() => {
    if (!map.current || !userLocation) return;

    updateUserMarker();

    // Find restaurants within 15 km (~9.32 miles)
    const nearbyStores = allStores.filter((s) => {
      if (!s.latitude || !s.longitude) return false;
      const dist = getDistance(userLocation.lat, userLocation.lng, s.latitude, s.longitude);
      return dist <= 9.32;
    });

    if (nearbyStores.length > 0) {
      // Fit bounds so both the user and the restaurant cluster are visible.
      // If the house is on the city edge, this "pulls" the viewport toward
      // the restaurant cluster (city center) — same effect as DoorDash / Uber Eats.
      const bounds = new mapboxgl.LngLatBounds();
      bounds.extend([userLocation.lng, userLocation.lat]);
      nearbyStores.forEach((s) => bounds.extend([s.longitude, s.latitude]));

      map.current.fitBounds(bounds, {
        padding: { top: 80, bottom: 280, left: 60, right: 60 },
        maxZoom: 13,
        duration: 1200,
        essential: true,
      });
    } else {
      // No nearby restaurants — stay centered on user location
      map.current.flyTo({
        center: [userLocation.lng, userLocation.lat],
        zoom: 11,
        essential: true,
      });
    }
  }, [userLocation, allStores, updateUserMarker]);

  // Update marker selection highlight when selectedStore changes
  useEffect(() => {
    markersRef.current.forEach((marker) => {
      const el = marker.getElement();
      const storeId = el.dataset.storeId;
      if (!storeId) return;
      const isSelected = selectedStore?.id === storeId;
      if (isSelected) {
        el.classList.add('selected');
      } else {
        el.classList.remove('selected');
      }
    });
  }, [selectedStore]);

  const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

  const loadStoreProducts = useCallback(async (storeId) => {
    setLoadingProducts(true);
    try {
      // Check localStorage cache
      const cacheKey = `ff_popular_items_${storeId}`;
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          if (parsed.timestamp && Date.now() - parsed.timestamp < CACHE_TTL_MS && Array.isArray(parsed.data)) {
            setStoreProducts(parsed.data);
            setLoadingProducts(false);
            return;
          }
        } catch (e) {
          // ignore corrupted cache
        }
      }

      const catsSnap = await getDocs(collection(db, 'publicStores', storeId, 'categories'));
      const prods = [];
      for (const catDoc of catsSnap.docs) {
        const prodSnap = await getDocs(collection(db, 'publicStores', storeId, 'categories', catDoc.id, 'products'));
        prodSnap.docs.forEach((p) => {
          prods.push({ id: p.id, categoryId: catDoc.id, ...p.data() });
        });
      }
      const top6 = prods.slice(0, 6);
      setStoreProducts(top6);
      // Save to cache
      localStorage.setItem(cacheKey, JSON.stringify({ timestamp: Date.now(), data: top6 }));
    } catch (error) {
      console.error('Error loading store products:', error);
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  const handleSelectStore = useCallback((store) => {
    setSelectedStore(store);
    loadStoreProducts(store.id);
  }, [loadStoreProducts]);

  // Mobile bottom sheet drag handling
  const sheetRef = useRef(null);
  // ── Bottom sheet drag logic (real-time transform) ──
  const dragState = useRef({
    active: false,
    startY: 0,
    startTranslate: 0,
    lastY: 0,
    lastTime: 0,
    velocity: 0,
    currentTranslate: 0,
  });

  const getSheetMetrics = useCallback(() => {
    const vh = window.innerHeight;
    const handleBar = sheetRef.current?.querySelector('.ff-bottom-sheet__handle-bar');
    const handleHeight = handleBar ? handleBar.getBoundingClientRect().height + 12 : 85;
    const sheetHeight = vh * 0.92;
    return {
      sheetHeight,
      handleHeight,
      collapsedTranslate: sheetHeight - handleHeight, // ocultamos todo menos handle
      halfTranslate: Math.max(0, sheetHeight - vh * 0.55),
      fullTranslate: 0,
    };
  }, []);

  const applySheetTransform = useCallback((translateY) => {
    if (!sheetRef.current) return;
    sheetRef.current.style.transform = `translateY(${translateY}px)`;
  }, []);

  // Snap to correct transform whenever sheetState changes
  useEffect(() => {
    const metrics = getSheetMetrics();
    let target = 0;
    if (sheetState === 'collapsed') target = metrics.collapsedTranslate;
    else if (sheetState === 'half') target = metrics.halfTranslate;
    else target = metrics.fullTranslate;

    requestAnimationFrame(() => {
      applySheetTransform(target);
    });
  }, [sheetState, getSheetMetrics, applySheetTransform]);

  const handleDragStart = useCallback((clientY) => {
    const metrics = getSheetMetrics();
    const currentTranslate =
      sheetState === 'collapsed'
        ? metrics.collapsedTranslate
        : sheetState === 'half'
        ? metrics.halfTranslate
        : metrics.fullTranslate;

    dragState.current = {
      active: true,
      startY: clientY,
      startTranslate: currentTranslate,
      lastY: clientY,
      lastTime: Date.now(),
      velocity: 0,
      currentTranslate,
    };

    if (sheetRef.current) {
      sheetRef.current.classList.add('ff-bottom-sheet--dragging');
    }
  }, [sheetState, getSheetMetrics]);

  const handleDragMove = useCallback((clientY) => {
    if (!dragState.current.active) return;
    const now = Date.now();
    const dt = now - dragState.current.lastTime;
    if (dt > 0) {
      dragState.current.velocity = (dragState.current.lastY - clientY) / dt; // px/ms, positive = up
    }
    dragState.current.lastY = clientY;
    dragState.current.lastTime = now;

    const metrics = getSheetMetrics();
    const deltaY = dragState.current.startY - clientY;
    let newTranslate = dragState.current.startTranslate - deltaY;

    // Clamp between full (0) and collapsed (max hidden)
    newTranslate = Math.max(metrics.fullTranslate, Math.min(metrics.collapsedTranslate, newTranslate));
    dragState.current.currentTranslate = newTranslate;
    applySheetTransform(newTranslate);
  }, [getSheetMetrics, applySheetTransform]);

  const handleDragEnd = useCallback(() => {
    if (!dragState.current.active) return;
    dragState.current.active = false;

    if (sheetRef.current) {
      sheetRef.current.classList.remove('ff-bottom-sheet--dragging');
    }

    const metrics = getSheetMetrics();
    const finalTranslate = dragState.current.currentTranslate;

    const deltaY = dragState.current.startY - dragState.current.lastY;
    const absDelta = Math.abs(deltaY);
    const velocity = dragState.current.velocity;
    const velocityThreshold = 0.5; // px/ms

    // If it was just a tap (moved < 10px), treat as a click → toggle state
    if (absDelta < 10) {
      setSheetState((prev) => {
        if (prev === 'collapsed') return 'half';
        if (prev === 'half') return 'full';
        return 'collapsed';
      });
      return;
    }

    let nextState = sheetState;

    // Fast swipe overrides position
    if (velocity > velocityThreshold) {
      // Swiped UP fast → advance state
      if (sheetState === 'collapsed') nextState = 'half';
      else if (sheetState === 'half') nextState = 'full';
    } else if (velocity < -velocityThreshold) {
      // Swiped DOWN fast → retreat state
      if (sheetState === 'full') nextState = 'half';
      else if (sheetState === 'half') nextState = 'collapsed';
    } else {
      // Position-based snap: compare finalTranslate to the three anchors
      // Use distances to each anchor, pick closest
      const dCollapsed = Math.abs(finalTranslate - metrics.collapsedTranslate);
      const dHalf = Math.abs(finalTranslate - metrics.halfTranslate);
      const dFull = Math.abs(finalTranslate - metrics.fullTranslate);

      if (dCollapsed <= dHalf && dCollapsed <= dFull) {
        nextState = 'collapsed';
      } else if (dHalf <= dFull) {
        nextState = 'half';
      } else {
        nextState = 'full';
      }
    }

    setSheetState(nextState);
  }, [sheetState, getSheetMetrics]);

  const handleCloseModal = () => {
    setSheetState('collapsed');
    setSelectedStore(null);
  };

  const handleLocateMe = () => {
    if (!map.current || !userLocation) return;
    map.current.flyTo({
      center: [userLocation.lng, userLocation.lat],
      zoom: 14,
      essential: true,
    });
  };

  // Filter visible stores by selected tags
  const filteredVisibleStores = useMemo(() => {
    if (selectedTags.length === 0) return visibleStores;
    return visibleStores.filter((store) =>
      selectedTags.every((tag) => (store.tags || []).includes(tag))
    );
  }, [visibleStores, selectedTags]);

  // Visible favorites within current map bounds (already tag-filtered via filteredVisibleStores)
  const visibleFavorites = useMemo(() => {
    return filteredVisibleStores.filter((s) => favoriteIds.has(s.id));
  }, [filteredVisibleStores, favoriteIds]);

  const formatCurrency = (val) => {
    const num = typeof val === 'number' ? val : parseFloat(val) || 0;
    return `$${num.toFixed(2)}`;
  };

  const distanceText = (store) => {
    if (!userLocation || !store.latitude || !store.longitude) return null;
    const d = getDistance(userLocation.lat, userLocation.lng, store.latitude, store.longitude);
    if (d >= 99999) return null;
    return d < 1 ? `${(d * 5280).toFixed(0)} ft` : `${d.toFixed(1)} mi`;
  };

  const renderStars = (rating) => {
    const rounded = Math.round(rating || 0);
    return (
      <span style={{ display: 'flex', gap: 2 }}>
        {[1,2,3,4,5].map((s) => (
          <i key={s} className="fas fa-star" style={{ color: s <= rounded ? '#ffc107' : '#e0e0e0', fontSize: '0.7rem' }}></i>
        ))}
      </span>
    );
  };

  const formatTime = (hour, minute) => {
    const h = hour || 0;
    const m = minute || 0;
    const ampm = h < 12 ? 'AM' : 'PM';
    const h12 = h % 12 || 12;
    return `${h12}:${m.toString().padStart(2, '0')} ${ampm}`;
  };

  const getTodayHoursInfo = (hours) => {
    if (!hours || hours.length === 0) return null;
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const todayIdx = new Date().getDay();
    const today = days[todayIdx];
    const todayHours = hours.filter(h => h.day === today);

    // Helper to find next open day and time
    const findNextOpen = (startOffset = 1) => {
      for (let i = 0; i < 7; i++) {
        const checkIdx = (todayIdx + startOffset + i) % 7;
        const checkDay = days[checkIdx];
        const checkHours = hours.filter(h => h.day === checkDay && !h.isClosed);
        if (checkHours.length > 0) {
          const first = checkHours[0];
          return {
            dayLabel: dayNames[checkIdx],
            timeLabel: formatTime(first.fromHour, first.fromMinute),
            isTomorrow: i === 0 && startOffset === 1,
          };
        }
      }
      return null;
    };

    if (todayHours.length === 0) {
      const next = findNextOpen(1);
      return next
        ? { status: 'closed', text: 'Closed today', hours: null, nextOpen: next.timeLabel, nextOpenDay: next.dayLabel }
        : { status: 'closed', text: 'Closed today', hours: null, nextOpen: null, nextOpenDay: null };
    }

    if (todayHours.some(h => h.isClosed)) {
      const next = findNextOpen(1);
      return next
        ? { status: 'closed', text: 'Closed today', hours: null, nextOpen: next.timeLabel, nextOpenDay: next.dayLabel }
        : { status: 'closed', text: 'Closed today', hours: null, nextOpen: null, nextOpenDay: null };
    }

    const ranges = todayHours.map(h => {
      const fromMin = (h.fromHour || 0) * 60 + (h.fromMinute || 0);
      const toMin = (h.toHour || 0) * 60 + (h.toMinute || 0);
      const fromLabel = formatTime(h.fromHour, h.fromMinute);
      const toLabel = formatTime(h.toHour, h.toMinute);
      return { fromMin, toMin, label: `${fromLabel} - ${toLabel}`, closesAtLabel: toLabel };
    });
    const now = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    let isOpen = false;
    let nextCloseLabel = null;
    for (const r of ranges) {
      if (r.toMin < r.fromMin) {
        if (nowMin >= r.fromMin || nowMin <= r.toMin) { isOpen = true; nextCloseLabel = r.closesAtLabel; }
      } else {
        if (nowMin >= r.fromMin && nowMin <= r.toMin) { isOpen = true; nextCloseLabel = r.closesAtLabel; }
      }
    }

    if (!isOpen) {
      // Check if there is another range later today
      const laterRange = ranges.find(r => nowMin < r.fromMin);
      if (laterRange) {
        return {
          status: 'closed',
          text: 'Closed now',
          hours: ranges.map(r => r.label).join('  ·  '),
          closesAt: null,
          nextOpen: laterRange.label.split(' - ')[0],
          nextOpenDay: 'Today',
        };
      }
      // Otherwise next open day
      const next = findNextOpen(1);
      return {
        status: 'closed',
        text: 'Closed now',
        hours: ranges.map(r => r.label).join('  ·  '),
        closesAt: null,
        nextOpen: next?.timeLabel || null,
        nextOpenDay: next?.dayLabel || null,
      };
    }

    return {
      status: 'open',
      text: 'Open now',
      hours: ranges.map(r => r.label).join('  ·  '),
      closesAt: nextCloseLabel,
      nextOpen: null,
      nextOpenDay: null,
    };
  };

  const renderTodayHours = (hours) => {
    const info = getTodayHoursInfo(hours);
    if (!info) return null;
    const isOpen = info.status === 'open';
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 14px',
        background: isOpen ? '#e8f5e9' : '#ffebee',
        borderRadius: 10,
        marginBottom: 12,
        border: `1px solid ${isOpen ? '#c8e6c9' : '#ffcdd2'}`,
      }}>
        <div style={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          background: isOpen ? '#4caf50' : '#f44336',
          flexShrink: 0,
          boxShadow: `0 0 0 3px ${isOpen ? 'rgba(76,175,80,0.2)' : 'rgba(244,67,54,0.2)'}`,
        }}></div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 700, color: isOpen ? '#2e7d32' : '#c62828' }}>
              {info.text}
            </span>
            {isOpen && info.closesAt && (
              <span style={{ fontSize: '0.8rem', color: '#555', fontWeight: 500 }}>
                ·  Closes at {info.closesAt}
              </span>
            )}
            {!isOpen && info.nextOpen && info.nextOpenDay && (
              <span style={{ fontSize: '0.8rem', color: '#555', fontWeight: 500 }}>
                ·  Opens at {info.nextOpen} {info.nextOpenDay}
              </span>
            )}
          </div>
          {info.hours && (
            <div style={{ fontSize: '0.75rem', color: '#777', marginTop: 3 }}>
              {info.hours}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderOperationHours = (hours) => {
    if (!hours || hours.length === 0) return null;
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const todayIdx = new Date().getDay();

    const todayRow = (() => {
      const dayH = hours.filter(h => h.day === days[todayIdx]);
      if (dayH.length === 0) return (
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#ff5722', fontWeight: 600 }}>
          <span>{dayLabels[todayIdx]} (Today)</span>
          <span>Hours not set</span>
        </div>
      );
      const text = dayH.some(h => h.isClosed)
        ? 'Closed'
        : dayH.map(h => {
            const from = formatTime(h.fromHour, h.fromMinute);
            const to = formatTime(h.toHour, h.toMinute);
            return `${from} - ${to}`;
          }).join(', ');
      return (
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#ff5722', fontWeight: 600 }}>
          <span>{dayLabels[todayIdx]} (Today)</span>
          <span>{text}</span>
        </div>
      );
    })();

    return (
      <div style={{ marginTop: 8 }}>
        <button
          onClick={() => setShowAllHours(v => !v)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            background: 'none',
            border: 'none',
            padding: '6px 0',
            fontSize: '0.85rem',
            fontWeight: 700,
            color: '#333',
            cursor: 'pointer',
            borderBottom: '1px solid #eee',
            marginBottom: 6,
          }}
        >
          <span>Hours</span>
          <i className={`fas fa-chevron-${showAllHours ? 'up' : 'down'}`} style={{ fontSize: '0.75rem', color: '#888' }}></i>
        </button>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {todayRow}
          {showAllHours && days.map((day, idx) => {
            if (idx === todayIdx) return null;
            const dayH = hours.filter(h => h.day === day);
            if (dayH.length === 0) return null;
            const text = dayH.some(h => h.isClosed)
              ? 'Closed'
              : dayH.map(h => {
                  const from = formatTime(h.fromHour, h.fromMinute);
                  const to = formatTime(h.toHour, h.toMinute);
                  return `${from} - ${to}`;
                }).join(', ');
            return (
              <div key={day} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#666', fontWeight: 400 }}>
                <span>{dayLabels[idx]}</span>
                <span>{text}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderStoreCard = (store) => (
    <div
      key={store.id}
      className={`ff-map-list-item ${selectedStore?.id === store.id ? 'active' : ''}`}
      onClick={() => handleSelectStore(store)}
    >
      <div className="ff-map-list-img">
        {store.design?.logoUrl || store.logoUrl ? (
          <img src={store.design?.logoUrl || store.logoUrl} alt={store.locationName} />
        ) : (
          <div className="ff-map-list-placeholder"><i className="fas fa-store"></i></div>
        )}
        {favoriteIds.has(store.id) && (
          <div className="ff-map-list-fav-badge"><i className="fas fa-heart"></i></div>
        )}
      </div>
      <div className="ff-map-list-body">
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <h4 className="ff-map-list-name" style={{ flex: 1 }}>{store.locationName}</h4>
          {favoriteIds.has(store.id) && <i className="fas fa-heart text-danger" style={{ fontSize: '0.75rem' }}></i>}
        </div>
        {(store.averageRating > 0) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            {renderStars(store.averageRating)}
            <span style={{ fontSize: '0.75rem', color: '#666', fontWeight: 600 }}>{store.averageRating.toFixed(1)} ({store.totalReviews || 0})</span>
          </div>
        )}
        <p className="ff-map-list-address">{store.address || store.city || ''}</p>
        <div className="ff-map-list-meta">
          {distanceText(store) && <span className="ff-map-list-distance">{distanceText(store)}</span>}
          {store.phone && <span className="ff-map-list-phone"><i className="fas fa-phone mr-1"></i>{store.phone}</span>}
        </div>
        {store.enableFodyesRewards === true && (
          <div className="ff-map-list-fodyes-badge" style={{ marginTop: 4 }}>
            <i className="fas fa-coins mr-1"></i>1 = ${(parseFloat(store.fodyesCoinValue) || 0).toFixed(2)}
            {userFodyes.backingValue > 0 && (parseFloat(store.fodyesCoinValue) || 0) > 0 && (
              <span style={{ fontWeight: 700 }}> · {Math.floor(userFodyes.backingValue / (parseFloat(store.fodyesCoinValue) || 0))} yours</span>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const renderStoreDetail = (store) => (
    <div className="ff-map-detail">
      <div className="ff-map-detail-header">
        {(store.design?.logoUrl || store.logoUrl) && (
          <img src={store.design?.logoUrl || store.logoUrl} alt={store.locationName} className="ff-map-detail-logo" />
        )}
        <div style={{ flex: 1 }}>
          <h3 className="ff-map-detail-name">{store.locationName}</h3>
          <p className="ff-map-detail-address">{store.address}</p>
          {(store.averageRating > 0) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
              {renderStars(store.averageRating)}
              <span style={{ fontSize: '0.8rem', color: '#666', fontWeight: 600 }}>{store.averageRating.toFixed(1)} ({store.totalReviews || 0} reviews)</span>
            </div>
          )}
        </div>
      </div>

      {/* Today's Hours — professional status badge */}
      {renderTodayHours(store.operationHours)}

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        {distanceText(store) && (
          <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#ff5722', background: '#fff3e0', padding: '4px 10px', borderRadius: 10 }}>
            <i className="fas fa-map-marker-alt mr-1"></i>{distanceText(store)}
          </span>
        )}
        {store.phone && (
          <span style={{ fontSize: '0.85rem', color: '#555' }}>
            <i className="fas fa-phone mr-1 text-orange"></i>{store.phone}
          </span>
        )}
        {store.enableFodyesRewards === true && (
          <span style={{ fontSize: '0.85rem', color: '#ff9800', background: '#fff8e1', padding: '4px 10px', borderRadius: 10, fontWeight: 600 }}>
            <i className="fas fa-coins mr-1"></i>1 = ${(parseFloat(store.fodyesCoinValue) || 0).toFixed(2)}
            {userFodyes.backingValue > 0 && (parseFloat(store.fodyesCoinValue) || 0) > 0 && (
              <span style={{ fontWeight: 700 }}> · {Math.floor(userFodyes.backingValue / (parseFloat(store.fodyesCoinValue) || 0))} yours</span>
            )}
          </span>
        )}
      </div>

      {renderOperationHours(store.operationHours)}

      <h4 className="ff-map-detail-section">Popular Items</h4>
      {loadingProducts ? (
        <div className="text-center py-3">
          <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 60, height: 60, objectFit: 'contain' }} />
        </div>
      ) : storeProducts.length > 0 ? (
        <div className="ff-map-detail-products-scroll">
          {storeProducts.map((p) => (
            <div key={p.id} className="ff-map-detail-product-card">
              <div className="ff-map-detail-product-img-wrap">
                {p.imageUrl ? (
                  <img src={p.imageUrl} alt={p.name} />
                ) : (
                  <div className="ff-map-detail-product-img-placeholder"><i className="fas fa-utensils"></i></div>
                )}
              </div>
              <div className="ff-map-detail-product-info">
                <div className="ff-map-detail-product-name">{p.name}</div>
                <div className="ff-map-detail-product-price">{formatCurrency(p.price)}</div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-muted">No products available</p>
      )}

      <button
        className="ff-btn ff-btn-primary ff-btn-block mt-3"
        onClick={() => {
          const slug = (store.locationName || 'store').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').substring(0, 60);
          navigate(`/store/${slug}-${store.id}`);
        }}
      >
        View Full Menu
      </button>
    </div>
  );

  return (
    <div className="ff-map-view">
      {/* Desktop: List + Map */}
      {!isMobile && (
        <div className="ff-map-desktop">
          <div className="ff-map-list">
            {favorites.length > 0 ? (
              <div style={{ display: 'flex', gap: 8, padding: '12px 16px', borderBottom: '1px solid #eee' }}>
                <button
                  className={`ff-map-tab ${activeTab === 'all' ? 'active' : ''}`}
                  onClick={() => setActiveTab('all')}
                >
                  <i className="fas fa-store mr-1"></i>All ({filteredVisibleStores.length})
                </button>
                <button
                  className={`ff-map-tab ${activeTab === 'favorites' ? 'active' : ''}`}
                  onClick={() => setActiveTab('favorites')}
                >
                  <i className="fas fa-heart mr-1"></i>Favorites ({visibleFavorites.length})
                </button>
              </div>
            ) : (
              <h3 className="ff-map-list-title">
                <i className="fas fa-store mr-2 text-orange"></i>
                {filteredVisibleStores.length} Restaurants
              </h3>
            )}
            {/* Desktop Tags Filter */}
            {tags.length > 0 && (
              <div className="ff-map-list-tags">
                <div className="ff-map-list-tags-scroll">
                  {tags.map((tag) => {
                    const isSelected = selectedTags.includes(tag.slug);
                    const iconClass = TAG_ICON_MAP[tag.slug] || 'fa-tag';
                    return (
                      <button
                        key={tag.id}
                        onClick={() => toggleTag(tag.slug)}
                        className={`ff-map-list-tag-badge ${isSelected ? 'selected' : ''}`}
                        title={tag.label || tag.slug}
                      >
                        <i className={`fas ${iconClass}`} />
                        <span>{tag.label || tag.slug}</span>
                      </button>
                    );
                  })}
                </div>
                {selectedTags.length > 0 && (
                  <button
                    onClick={clearFilters}
                    className="ff-map-list-tag-clear"
                  >
                    Clear
                  </button>
                )}
              </div>
            )}
            <div className="ff-map-list-scroll">
              {loadingMap ? (
                <div className="text-center py-5 text-muted">
                  <div className="ff-spinner" style={{ width: 32, height: 32, margin: '0 auto 12px' }}></div>
                  <p>Loading map...</p>
                </div>
              ) : (
                <>
                  {activeTab === 'favorites'
                    ? visibleFavorites.map(renderStoreCard)
                    : filteredVisibleStores.map(renderStoreCard)
                  }
                  {activeTab === 'favorites' && visibleFavorites.length === 0 && (
                    <div className="text-center py-5 text-muted">
                      <i className="far fa-heart fa-2x mb-2" style={{ opacity: 0.3 }}></i>
                      <p>No favorites in this area.</p>
                    </div>
                  )}
                  {activeTab === 'all' && filteredVisibleStores.length === 0 && !loadingMap && (
                    <div className="text-center py-5 text-muted">
                      <i className="fas fa-search-location fa-2x mb-2" style={{ opacity: 0.3 }}></i>
                      <p>Move the map to explore more restaurants.</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="ff-map-wrap">
            <div ref={mapContainer} className="ff-map-container" />
            {mapError && (
              <div style={{
                position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
                background: '#fff3e0', border: '1px solid #ffb74d', color: '#e65100',
                padding: '8px 14px', borderRadius: 10, fontSize: '0.8rem', fontWeight: 600,
                zIndex: 5, boxShadow: '0 2px 8px rgba(0,0,0,0.12)', maxWidth: '90%', textAlign: 'center',
              }}>
                <i className="fas fa-triangle-exclamation mr-2"></i>{mapError}
              </div>
            )}
            {userLocation && (
              <button className="ff-map-locate-btn" onClick={handleLocateMe} title="Center on my location">
                <i className="fas fa-crosshairs"></i>
              </button>
            )}
            {selectedStore && (
              <div className="ff-map-location-card">
                <button className="ff-map-location-card-close" onClick={() => setSelectedStore(null)}>
                  <i className="fas fa-times"></i>
                </button>
                {renderStoreDetail(selectedStore)}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mobile: Map + Persistent Bottom Sheet */}
      {isMobile && (
        <>
          <div className="ff-map-wrap">
            <div ref={mapContainer} className="ff-map-container-mobile" />
            {mapError && (
              <div style={{
                position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
                background: '#fff3e0', border: '1px solid #ffb74d', color: '#e65100',
                padding: '8px 14px', borderRadius: 10, fontSize: '0.75rem', fontWeight: 600,
                zIndex: 5, boxShadow: '0 2px 8px rgba(0,0,0,0.12)', maxWidth: '90%', textAlign: 'center',
              }}>
                <i className="fas fa-triangle-exclamation mr-2"></i>{mapError}
              </div>
            )}
            {userLocation && (
              <button className="ff-map-locate-btn" onClick={handleLocateMe} title="Center on my location">
                <i className="fas fa-crosshairs"></i>
              </button>
            )}
          </div>

          {/* Persistent Bottom Sheet */}
          <div
            ref={sheetRef}
            className={`ff-bottom-sheet ff-bottom-sheet--${sheetState}`}
            style={{ transform: undefined }} /* managed by JS */
          >
            {/* Drag Handle — only interactive area */}
            <div
              className="ff-bottom-sheet__handle-bar"
              onTouchStart={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (e.nativeEvent?.stopImmediatePropagation) e.nativeEvent.stopImmediatePropagation();
                // Disable map pan/zoom so the swipe only moves the sheet
                if (map.current) {
                  try { map.current.dragPan.disable(); } catch (_) {}
                  try { map.current.scrollZoom.disable(); } catch (_) {}
                }
                handleDragStart(e.touches[0].clientY);
              }}
              onTouchMove={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (e.nativeEvent?.stopImmediatePropagation) e.nativeEvent.stopImmediatePropagation();
                handleDragMove(e.touches[0].clientY);
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (e.nativeEvent?.stopImmediatePropagation) e.nativeEvent.stopImmediatePropagation();
                // Re-enable map interactions
                if (map.current) {
                  try { map.current.dragPan.enable(); } catch (_) {}
                  try { map.current.scrollZoom.enable(); } catch (_) {}
                }
                handleDragEnd();
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (e.nativeEvent?.stopImmediatePropagation) e.nativeEvent.stopImmediatePropagation();
                if (map.current) {
                  try { map.current.dragPan.disable(); } catch (_) {}
                  try { map.current.scrollZoom.disable(); } catch (_) {}
                }
                handleDragStart(e.clientY);
                // Mouse needs window-level move/up because mouse leaves element
                const onMove = (ev) => handleDragMove(ev.clientY);
                const onUp = () => {
                  window.removeEventListener('mousemove', onMove);
                  window.removeEventListener('mouseup', onUp);
                  if (map.current) {
                    try { map.current.dragPan.enable(); } catch (_) {}
                    try { map.current.scrollZoom.enable(); } catch (_) {}
                  }
                  handleDragEnd();
                };
                window.addEventListener('mousemove', onMove);
                window.addEventListener('mouseup', onUp);
              }}
            >
              <div className="ff-bottom-sheet__handle" />
              <div className="ff-bottom-sheet__title-row">
                <span className="ff-bottom-sheet__title">
                  <i className="fas fa-utensils mr-2 text-orange" />
                  {sheetState === 'collapsed'
                    ? `${filteredVisibleStores.length} Restaurants`
                    : 'Restaurant List'}
                </span>
                {selectedTags.length > 0 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); clearFilters(); }}
                    style={{
                      background: 'none', border: 'none', color: '#ff5722',
                      fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="ff-bottom-sheet__content">
              {/* Tags Section (hidden in collapsed) */}
              {sheetState !== 'collapsed' && tags.length > 0 && (
                <div className="ff-bottom-sheet__tags">
                  <div className="ff-bottom-sheet__section-label">Filter by Tag</div>
                  <div className="ff-bottom-sheet__tags-scroll">
                    {tags.map((tag) => {
                      const isSelected = selectedTags.includes(tag.slug);
                      const iconClass = TAG_ICON_MAP[tag.slug] || 'fa-tag';
                      return (
                        <button
                          key={tag.id}
                          onClick={() => toggleTag(tag.slug)}
                          className={`ff-bottom-sheet__tag-badge ${isSelected ? 'selected' : ''}`}
                        >
                          <i className={`fas ${iconClass}`} />
                          <span>{tag.label || tag.slug}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Store List */}
              <div className="ff-bottom-sheet__stores">
                {sheetState !== 'collapsed' && (
                  <div className="ff-bottom-sheet__section-label">
                    {activeTab === 'favorites' ? 'Favorites' : 'All Restaurants'}
                    <span style={{ fontSize: '0.75rem', color: '#888', marginLeft: 6 }}>
                      ({(activeTab === 'favorites' ? visibleFavorites : filteredVisibleStores).length})
                    </span>
                  </div>
                )}

                {/* Tab switcher (only in half/full) */}
                {sheetState !== 'collapsed' && favorites.length > 0 && (
                  <div className="ff-bottom-sheet__tabs">
                    <button
                      className={`ff-bottom-sheet__tab ${activeTab === 'all' ? 'active' : ''}`}
                      onClick={() => setActiveTab('all')}
                    >
                      All
                    </button>
                    <button
                      className={`ff-bottom-sheet__tab ${activeTab === 'favorites' ? 'active' : ''}`}
                      onClick={() => setActiveTab('favorites')}
                    >
                      Favorites
                    </button>
                  </div>
                )}

                {/* Cards */}
                <div className="ff-bottom-sheet__store-list">
                  {loadingMap ? (
                    <div className="text-center py-3 text-muted">
                      <div className="ff-spinner" style={{ width: 28, height: 28, margin: '0 auto 8px' }} />
                      <p style={{ fontSize: '0.8rem' }}>Loading...</p>
                    </div>
                  ) : (
                    <>
                      {(activeTab === 'favorites' ? visibleFavorites : filteredVisibleStores).map((store) => (
                        <div
                          key={store.id}
                          className={`ff-bottom-sheet__store-card ${selectedStore?.id === store.id ? 'active' : ''}`}
                          onClick={() => {
                            handleSelectStore(store);
                            if (sheetState === 'collapsed') setSheetState('half');
                          }}
                        >
                          <div className="ff-bottom-sheet__store-img">
                            {store.design?.logoUrl || store.logoUrl ? (
                              <img src={store.design?.logoUrl || store.logoUrl} alt={store.locationName} />
                            ) : (
                              <div className="ff-bottom-sheet__store-img-placeholder">
                                <i className="fas fa-store" />
                              </div>
                            )}
                          </div>
                          <div className="ff-bottom-sheet__store-info">
                            <div className="ff-bottom-sheet__store-header">
                              <h4 className="ff-bottom-sheet__store-name">{store.locationName}</h4>
                              {favoriteIds.has(store.id) && (
                                <i className="fas fa-heart" style={{ color: '#ff1744', fontSize: '0.75rem' }} />
                              )}
                            </div>
                            {(store.averageRating > 0) && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                                {renderStars(store.averageRating)}
                                <span style={{ fontSize: '0.7rem', color: '#666' }}>
                                  {store.averageRating.toFixed(1)}
                                </span>
                              </div>
                            )}
                            <div className="ff-bottom-sheet__store-meta">
                              {distanceText(store) && (
                                <span className="ff-bottom-sheet__store-distance">{distanceText(store)}</span>
                              )}
                              {store.tags && store.tags.length > 0 && (
                                <span className="ff-bottom-sheet__store-tags-mini">
                                  {store.tags.slice(0, 2).map((t) => (
                                    <span key={t} className="ff-bottom-sheet__store-tag-dot">{t}</span>
                                  ))}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      {(activeTab === 'favorites' ? visibleFavorites : filteredVisibleStores).length === 0 && !loadingMap && (
                        <div className="text-center py-4 text-muted">
                          <i className="fas fa-search-location fa-lg mb-2" style={{ opacity: 0.3 }} />
                          <p style={{ fontSize: '0.8rem' }}>Move the map to explore more restaurants.</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Collapsed peek info: only show count, no scroll content */}
            {sheetState === 'collapsed' && (
              <div className="ff-bottom-sheet__peek">
                <span style={{ fontSize: '0.75rem', color: '#888' }}>
                  Swipe up to see list
                </span>
              </div>
            )}
          </div>

          {/* Mobile detail overlay (when a store is selected) */}
          {selectedStore && (
            <div className="ff-modal-overlay" onClick={() => setSelectedStore(null)}>
              <div className="ff-modal ff-modal-bottom" onClick={(e) => e.stopPropagation()}>
                <div className="ff-modal-header">
                  <div className="ff-modal-drag-handle" />
                  <button className="ff-modal-close" onClick={() => setSelectedStore(null)}>
                    <i className="fas fa-times" />
                  </button>
                </div>
                <div className="ff-modal-body">
                  {renderStoreDetail(selectedStore)}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MapView;
